import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const ROUND_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#06b6d4', '#84cc16', '#ec4899', '#14b8a6'];

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 17);
      return;
    }
    const lats = points.map(p => p.lat);
    const lngs = points.map(p => p.lng);
    map.fitBounds(
      [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]],
      { padding: [60, 60], maxZoom: 17 }
    );
  }, [points, map]);
  return null;
};

export const CanvassMap = ({ data }) => {
  const roundColor = useMemo(() => {
    const rounds = [...new Set((data?.existing || []).map(e => e.round).filter(Boolean))];
    return Object.fromEntries(rounds.map((r, i) => [r, ROUND_COLORS[i % ROUND_COLORS.length]]));
  }, [data]);

  if (!data?.canvassed?.length) return null;

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 mb-4 bg-white" data-testid="canvassing-map">
      <MapContainer
        center={[data.canvassed[0].lat, data.canvassed[0].lng]}
        zoom={15}
        style={{ height: 320, width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {/* Zoom to the canvassed pins only — fitting every nearby customer
            zooms out too far to read street names. */}
        <FitBounds points={data.canvassed} />
        {data.existing.map((e, i) => (
          <CircleMarker
            key={`e${i}`}
            center={[e.lat, e.lng]}
            radius={6}
            pathOptions={{
              color: '#ffffff',
              weight: 1,
              fillColor: roundColor[e.round] || '#94a3b8',
              fillOpacity: 0.85,
            }}
          >
            <Tooltip>{e.name}{e.round ? ` · ${e.round}` : ''}</Tooltip>
          </CircleMarker>
        ))}
        {data.canvassed.map((c, i) => (
          <CircleMarker
            key={`c${i}`}
            center={[c.lat, c.lng]}
            radius={10}
            pathOptions={{ color: '#7f1d1d', weight: 2, fillColor: '#ef4444', fillOpacity: 0.95 }}
          >
            <Tooltip permanent direction="top" offset={[0, -8]} className="canvass-pin-label">
              {c.name || 'New'}
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
      <div className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600 border-t border-slate-100">
        <span className="flex items-center gap-1.5 font-semibold text-slate-700">
          <span className="w-3 h-3 rounded-full bg-red-500 border border-red-900 inline-block" /> New canvassed
        </span>
        {Object.entries(roundColor).map(([r, col]) => (
          <span key={r} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: col }} /> {r}
          </span>
        ))}
        {data.failed > 0 && (
          <span className="text-amber-600">{data.failed} address{data.failed !== 1 ? 'es' : ''} couldn't be pinned</span>
        )}
      </div>
    </div>
  );
};
